// ── Bike specs and color matching for Settings › Website (1 Nov) ──────────────
//
// SPEC_GROUPS: the specs a shop can pick from, grouped the way a spec sheet
// reads, each with an example that shows in the empty box. Anything not here
// is added as a custom spec with its own name (Manny, 1 Nov: the first list
// was too short, and shops need their own entries too).
//
// STARTER_SPECS: the usual specs for each website category, added in one
// click as empty rows to fill in. Rows left empty are not saved.
//
// likelyColors: Lightspeed lists each color of a bike as its own item, so
// "Micro Ripper 12 Sparkling White" and "Micro Ripper 12 Silver Streak" arrive
// as two bikes. Same brand and the same first words of the name means it is
// probably the same model in another color, as long as the numbers in the
// names agree: "Coast Trail 20" and "Coast Trail 24", or "1SP" and "7SP", are
// different bikes. 27.5 and 29 are the exception, because Marin builds one
// model on 27.5" wheels in small sizes and 29" in large (Bolinas Ridge 2,
// Rift Zone 1).

export const SPEC_GROUPS = [
  { group: 'Frame and fit', specs: [
    ['Frame', 'Carbon, Super Boost 157mm'], ['Frame material', 'Aluminum'], ['Fork', 'Fox 36 Performance, 160mm'],
    ['Sizes', 'XS to XL'], ['Rider height', `5'4" to 6'2"`], ['Recommended age', '5 to 8 years'],
    ['Weight', '31.5 lb (size M)'], ['Max weight', '300 lb rider and cargo'], ['Tire clearance', 'Up to 29 x 2.6"'],
    ['Head tube angle', '64.5°'], ['Seat tube angle', '77°'], ['Reach', '475mm (size M)'], ['Stack', '625mm (size M)'],
    ['Chainstay length', '435mm'], ['Wheelbase', '1,230mm (size M)'], ['Color', 'Doctor Purple'],
  ] },
  { group: 'Suspension', specs: [
    ['Rear travel', '150mm'], ['Front travel', '160mm'], ['Rear shock', 'Fox Float X Performance'],
    ['Suspension design', 'DW-Link'], ['Lockout', 'Handlebar remote'],
  ] },
  { group: 'Drivetrain', specs: [
    ['Drivetrain', 'SRAM Eagle 70 Transmission'], ['Speeds', '1 x 12'], ['Gearing', '25/9'], ['Shifter', 'Shimano Deore'],
    ['Rear derailleur', 'Shimano Deore'], ['Front derailleur', 'Shimano 105'], ['Crankset', 'SRAM Eagle 70, 170mm'],
    ['Chainring', '32T'], ['Cassette', '10-52T'], ['Chain', 'Shimano 12-speed'], ['Bottom bracket', 'Threaded BSA'],
    ['Pedals', 'Flat pedals included'],
  ] },
  { group: 'Brakes', specs: [
    ['Brakes', 'Shimano 4-piston hydraulic disc'], ['Brake rotors', '203mm front, 180mm rear'],
  ] },
  { group: 'Wheels and tires', specs: [
    ['Wheel size', '29"'], ['Wheels', 'DT Swiss M1900'], ['Rims', 'Alloy, 30mm inner width'], ['Hubs', 'Shimano MT410'],
    ['Tires', 'Maxxis Minion DHF 29 x 2.5'], ['Front tire', 'Maxxis Assegai 29 x 2.5'], ['Rear tire', 'Maxxis Dissector 29 x 2.4'],
    ['Tubeless ready', 'Yes, rims taped and valves included'],
  ] },
  { group: 'Cockpit and seating', specs: [
    ['Handlebar', '800mm, 30mm rise'], ['Stem', '40mm'], ['Grips', 'Lock-on'], ['Bar tape', 'Cork'], ['Headset', 'Integrated'],
    ['Seatpost', '27.2mm alloy'], ['Dropper post', '170mm (size M)'], ['Saddle', 'WTB Volt'],
  ] },
  { group: 'E-bike', specs: [
    ['Motor', 'Shimano EP801, 85 Nm'], ['Battery', '630 Wh, removable'], ['Range', 'Up to 60 miles'],
    ['Class', 'Class 1, pedal assist to 20 mph'], ['Top assisted speed', '20 mph'], ['Throttle', 'Thumb throttle'],
    ['Display', 'Color display with USB-C'], ['Assist modes', 'Eco, Trail, Boost'], ['Charger', '4A'], ['Charge time', 'About 5 hours'],
  ] },
  { group: 'Extras', specs: [
    ['Fenders', 'Full coverage'], ['Rack', 'Rear rack, 55 lb'], ['Kickstand', 'Included'], ['Lights', 'Front and rear, integrated'],
    ['Bell', 'Included'], ['Chain guard', 'Included'], ['Mounts', 'Rack, fender and bottle mounts'],
    ['Included accessories', 'Training wheels'], ['Warranty', 'Lifetime frame warranty'],
  ] },
];

const EXAMPLES = new Map(SPEC_GROUPS.flatMap((g) => g.specs));
export const specExample = (label) => EXAMPLES.get(label) || '';
export const isKnownSpec = (label) => EXAMPLES.has(label);

export const STARTER_SPECS = {
  'Mountain': ['Frame', 'Fork', 'Rear shock', 'Rear travel', 'Front travel', 'Wheel size', 'Drivetrain', 'Brakes', 'Tires', 'Dropper post', 'Weight'],
  'Road & Gravel': ['Frame', 'Fork', 'Drivetrain', 'Brakes', 'Wheels', 'Tires', 'Tire clearance', 'Weight'],
  'Electric': ['Motor', 'Battery', 'Range', 'Class', 'Display', 'Drivetrain', 'Brakes', 'Tires', 'Weight'],
  'Commuter & Hybrid': ['Frame', 'Drivetrain', 'Brakes', 'Tires', 'Mounts', 'Weight'],
  'Kids': ['Wheel size', 'Recommended age', 'Rider height', 'Frame', 'Speeds', 'Brakes', 'Weight'],
  'BMX': ['Frame', 'Fork', 'Wheel size', 'Gearing', 'Brakes', 'Tires'],
};
export const starterFor = (category) => STARTER_SPECS[category] || ['Frame', 'Drivetrain', 'Brakes', 'Wheel size', 'Tires', 'Weight'];

const words = (name) => String(name || '').toLowerCase().replace(/[^a-z0-9.\s]/g, ' ').split(/\s+/).filter(Boolean);
const numbers = (name) => words(name).filter((w) => /\d/.test(w) && w !== '27.5' && w !== '29').sort().join(' ');
const brandOf = (b) => String((b.content && b.content.brand) || b.brand || '').toLowerCase().trim();

// How many words two names share from the start.
export function sharedStart(a, b) {
  const x = words(a); const y = words(b);
  let n = 0;
  while (n < x.length && n < y.length && x[n] === y[n]) n++;
  return n;
}

// Bikes that are probably this bike in another color, best match first.
// Only bikes that are free to add: not this one, not already folded into a
// bike, and not carrying colors of their own.
export function likelyColors(bike, bikes) {
  const brand = brandOf(bike);
  return bikes
    .filter((b) => b.key !== bike.key && !b.mergedInto && !bikes.some((x) => x.mergedInto === b.key))
    .filter((b) => brandOf(b) === brand)
    .map((b) => ({ b, n: sharedStart(bike.name, b.name) }))
    .filter(({ b, n }) => n >= 2 && numbers(b.name) === numbers(bike.name))
    .sort((p, q) => q.n - p.n || p.b.name.localeCompare(q.b.name))
    .map(({ b }) => b);
}

// ── Paste a spec sheet (4 Nov) ───────────────────────────────────────────────
//
// Manny, 4 Nov: filling specs from brand pages by reading those pages would
// need upkeep every time a brand redesigns, so instead the shop copies the
// specs from any page (brand site, dealer portal, a PDF) and pastes them, and
// this splits them into rows. Copied text comes in three shapes:
//   a table      "Frame<TAB>Series 1 6061 aluminum" (what copying a web table gives)
//   name: value  "Frame: Series 1 6061 aluminum"
//   two lines    "Frame" then "Series 1 6061 aluminum" (copied from a styled list)
// A table with a column per size (geometry) becomes one row per measurement:
// "Reach: S 380 · M 400 · L 415 · XL 430". Headings and notices ("Build Specs",
// "Specs subject to change") are left out.

const NOISE = /^(specifications?|specs|build( specs| kit)?|components?|geometry|bike geometry|size chart|product details|details|features|show (more|less|all)|view (all|more)|read more|.*subject to change.*|.*\bmay vary\b.*)$/i;

export function parseSpecSheet(text) {
  const lines = String(text || '').replace(/\r/g, '').split('\n').map((l) => l.replace(/ /g, ' ').trim()).filter(Boolean);
  const clean = (s, n) => s.replace(/\s+/g, ' ').replace(/[:\s]+$/, '').trim().slice(0, n);
  const out = [];
  let skipped = 0;
  const push = (label, value) => {
    const l = clean(label, 60); const v = String(value || '').replace(/\s+/g, ' ').trim().slice(0, 200);
    if (l && v && !NOISE.test(l)) out.push({ label: l, value: v }); else skipped += 1;
  };
  const body = lines.filter((l) => { if (NOISE.test(l.replace(/\t+/g, ' ').trim())) { skipped += 1; return false; } return true; });

  // A table: tabs between the cells.
  if (body.some((l) => l.includes('\t'))) {
    let header = null;
    for (const l of body) {
      const cells = l.split('\t').map((c) => c.trim()).filter(Boolean);
      if (cells.length < 2) { skipped += 1; continue; }
      if (cells.length === 2) { push(cells[0], cells[1]); continue; }
      // Several columns: the first such row names them (sizes), the rest are measurements.
      if (!header || header.length !== cells.length) { header = cells; continue; }
      push(cells[0], cells.slice(1).map((v, i) => `${header[i + 1]} ${v}`).join(' · '));
    }
    return { rows: out, skipped };
  }
  // name: value
  const colon = body.filter((l) => /^[^:]{1,40}:\s*\S/.test(l)).length;
  if (colon && colon >= body.length / 2) {
    for (const l of body) {
      const m = /^([^:]{1,40}):\s*(.+)$/.exec(l);
      if (m) push(m[1], m[2]); else skipped += 1;
    }
    return { rows: out, skipped };
  }
  // A name on one line, its value on the next.
  for (let i = 0; i + 1 < body.length; i += 2) {
    if (body[i].length > 40) { skipped += 1; i -= 1; continue; } // not a name: realign on the next line
    push(body[i], body[i + 1]);
  }
  if (body.length % 2 === 1 && out.length * 2 + skipped < body.length) skipped += 1;
  return { rows: out, skipped };
}

// Pasted rows into the bike's specs: a spec it already has (same name, even
// an empty row the shop added) gets the new value in its place; the others
// are added at the end, up to 30 specs with something in them.
export function mergeSpecs(current, rows, max = 30) {
  const next = (current || []).map((s) => ({ ...s }));
  const key = (l) => String(l || '').trim().toLowerCase();
  const filled = () => next.filter((s) => String(s.value || '').trim()).length;
  let updated = 0; let added = 0; let dropped = 0;
  for (const r of rows) {
    const at = next.findIndex((s) => key(s.label) === key(r.label));
    if (at >= 0) {
      if (String(next[at].value || '').trim()) { if (next[at].value !== r.value) updated += 1; } else added += 1;
      next[at] = { ...next[at], value: r.value };
      continue;
    }
    if (filled() >= max) { dropped += 1; continue; }
    next.push({ label: r.label, value: r.value });
    added += 1;
  }
  // Rows that were never named or filled in go.
  return { specs: next.filter((s) => key(s.label) || String(s.value || '').trim()).slice(0, Math.max(max, next.length)), updated, added, dropped };
}
